import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';

interface UseMobileProviderProps {
  children: ReactNode;
}

interface USeMobileContextProps {
  isMobile: boolean;
  setIsMobile: (value: boolean) => void;
}

export const IsMobileContext = createContext<USeMobileContextProps>(
  {} as USeMobileContextProps,
);

function detectMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();
  const hasMobileUA = /android|iphone|ipad|ipod|mobile/.test(ua);
  const supportsMatchMedia = typeof window.matchMedia === 'function';
  const coarsePointer = supportsMatchMedia ? window.matchMedia('(pointer: coarse)').matches : false;
  const narrowViewport = supportsMatchMedia ? window.matchMedia('(max-width: 980px)').matches : false;

  return hasMobileUA || coarsePointer || narrowViewport;
}

function addMediaQueryListener(query: MediaQueryList, callback: () => void) {
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', callback);
    return () => query.removeEventListener('change', callback);
  }

  query.addListener(callback);
  return () => query.removeListener(callback);
}

export function IsMobileProvider({ children }: UseMobileProviderProps) {
  const [isMobile, setIsMobile] = useState<boolean>(() => detectMobileDevice());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const onUpdate = () => {
      setIsMobile(detectMobileDevice());
    };

    let removeViewportListener = () => {};
    let removePointerListener = () => {};

    if (typeof window.matchMedia === 'function') {
      const viewportQuery = window.matchMedia('(max-width: 980px)');
      const pointerQuery = window.matchMedia('(pointer: coarse)');
      removeViewportListener = addMediaQueryListener(viewportQuery, onUpdate);
      removePointerListener = addMediaQueryListener(pointerQuery, onUpdate);
    }

    window.addEventListener('resize', onUpdate);
    window.addEventListener('orientationchange', onUpdate);

    return () => {
      removeViewportListener();
      removePointerListener();
      window.removeEventListener('resize', onUpdate);
      window.removeEventListener('orientationchange', onUpdate);
    };
  }, []);

  const value = useMemo(() => ({ isMobile, setIsMobile }), [isMobile]);

  return (
    <IsMobileContext.Provider value={value}>
      {children}
    </IsMobileContext.Provider>
  );
}
